// frontend/app/components/lessonPlans/LessonBrowseTree.js
//
// The second way into a lesson: Biology → Year 10 → the lessons.
//
// WHY THIS EXISTS. The finder replaced a tile grid with a box, which fixed a
// wall of fifty cards and broke something else on the way past: with nothing
// typed there is no way to see what the library holds at all. A teacher who
// does not already know a lesson exists cannot find it by describing it.
//
// WHY IT IS CLOSED. The fix must not undo the thing it is fixing. A tree that
// renders open is the tile grid again, one indent deeper — the resting state of
// the page has to stay one question and one box. So this is a quiet line under
// the box, and a teacher who only ever types never sees anything else.
//
// WHY IT IS SECONDARY IN LOOK, NOT ONLY IN PLACEMENT. The toggle is muted text
// with no border and no fill, and the subject chips are outlines rather than
// the Saras blue the box wears. Two controls of equal weight under one another
// is two primary paths, and then neither is.
//
// THE LEAF IS THE FINDER'S OWN ROW (`./LessonResultRow`). A lesson reached by
// browsing and the same lesson reached by typing are the same lesson, and must
// look like it.

import React, { useEffect, useMemo, useState } from "react";
import { ScrollView, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { MaterialCommunityIcons } from "@expo/vector-icons";

import ResultRow, { fill } from "./LessonResultRow";
import {
  UNPLACED_GRADE,
  UNPLACED_SUBJECT,
  groupLessonsForBrowse,
} from "./lessonBrowseGroups";

/**
 * How tall an opened tree may get inside a dashboard column.
 *
 * On the lessons index the tree IS the page, so it runs as long as it likes and
 * the page scrolls. On a dashboard it is one section stacked above Missions,
 * Journeys and the rest — a subject with twenty-one lessons in a year would
 * push all of them off the screen, so the leaf list is capped and scrolls
 * inside itself. Nothing is dropped: every lesson is still reachable, it is the
 * viewport that is bounded, not the list.
 */
const SECTION_LEAF_MAX = 340;

/** Matches the finder's box so the tree lines up under what opened it. */
const PAGE_BOX_MAX = 680;

/**
 * Who is browsing. The label is the same phrase for both because "browse by
 * subject" is what the control does and inventing a second name for students
 * would be worse copy, not more considerate. What changes is whose library it
 * is: a teacher browses the library they teach from, a student browses what
 * they are allowed to open.
 */
const BROWSE_COPY = {
  teacher: {
    label: "Or browse by subject",
    hint: "Every lesson in the library, by subject and year.",
  },
  student: {
    label: "Or browse by subject",
    hint: "Every lesson you can open, by subject and year.",
  },
};

/**
 * What a school calls the year a lesson is for, per board.
 *
 * Not one word slotted into one template: whole clauses, one per board, the way
 * `FINDER_COPY` and `BROWSE_COPY` are written. A `{word} {grade}` composition
 * would hand a translator "Class" and "{word} {grade}" separately, which is two
 * fragments that only reassemble in English word order — and it is exactly the
 * word order that differs elsewhere.
 *
 * Grade is the default rather than a fourth case, because it is the most widely
 * understood of the three and because a board nobody has mapped yet is far more
 * likely to be American than not. It is also what the teacher who asked for
 * this control said out loud.
 *
 * `base_board` keys into database.CONTENT_DB_URLS, so the keys here are its
 * keys. `anatomy`, `saras`, `lnc` and `ucsd` are deliberately absent: nobody
 * has said what those call a school year, and inventing an answer for them
 * would be worse than the honest default.
 */
const GRADE_COPY = {
  igcse: { label: "Year {grade}", unset: "Year not set" },
  cbse: { label: "Class {grade}", unset: "Class not set" },
  ap: { label: "Grade {grade}", unset: "Grade not set" },
  ngss_hs: { label: "Grade {grade}", unset: "Grade not set" },
};

const DEFAULT_GRADE_COPY = { label: "Grade {grade}", unset: "Grade not set" };

const gradeCopyFor = (board) =>
  GRADE_COPY[String(board || "").toLowerCase()] || DEFAULT_GRADE_COPY;

/**
 * One tappable node — a subject or a year — with the count that decides whether
 * the tap is worth making.
 *
 * The count is a separate pill rather than "Biology (27)" in one string so the
 * number reads as data and the name reads as a name at a glance; the two are
 * put back together for a screen reader, which gets one clause.
 */
const BrowseChip = ({ testID, label, count, colors, onPress, t }) => (
  <TouchableOpacity
    testID={testID}
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={fill(t("{label}, {count} lessons"), { label, count })}
    style={[styles.chip, { backgroundColor: colors.cardBg, borderColor: colors.border }]}
  >
    <Text style={[styles.chipLabel, { color: colors.textPrimary }]}>{label}</Text>
    <View style={[styles.chipCount, { backgroundColor: colors.subtleBg }]}>
      <Text style={[styles.chipCountText, { color: colors.textMuted }]}>{count}</Text>
    </View>
    <MaterialCommunityIcons name="chevron-right" size={15} color={colors.textMuted} />
  </TouchableOpacity>
);

const Crumb = ({ testID, label, colors, onPress, t }) =>
  onPress ? (
    <TouchableOpacity
      testID={testID}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={fill(t("Back to {label}"), { label })}
    >
      <Text style={[styles.crumbLink, { color: colors.accentPrimary }]}>{label}</Text>
    </TouchableOpacity>
  ) : (
    <Text style={[styles.crumbCurrent, { color: colors.textSecondary }]}>{label}</Text>
  );

const LessonBrowseTree = ({
  lessons = [],
  progressByLesson = {},
  colors,
  isDarkMode = false,
  // Controlled by the finder, because the finder has to know: an open tree and
  // a list of search results are two answers to the same question, and only one
  // of them may be on screen. See LessonFinder's `showResults`.
  open = false,
  onToggle,
  onOpenLesson,
  onEditLesson,
  audience = "teacher",
  layout = "section",
  t = (value) => value,
}) => {
  const translate = t;
  const copy = BROWSE_COPY[audience] || BROWSE_COPY.teacher;
  const isPage = layout === "page";

  // Where in the tree, held here rather than in the finder: nothing outside
  // this control needs to know which subject is expanded.
  const [subjectKey, setSubjectKey] = useState(null);
  const [gradeKey, setGradeKey] = useState(null);

  // Closing resets the walk. Reopening onto a subject somebody drilled into
  // five minutes ago reads as the tree having ignored the tap that closed it,
  // and the way back down is two taps.
  useEffect(() => {
    if (!open) {
      setSubjectKey(null);
      setGradeKey(null);
    }
  }, [open]);

  const groups = useMemo(() => groupLessonsForBrowse(lessons), [lessons]);

  const subject = groups.find((group) => group.key === subjectKey) || null;
  const grade = subject
    ? subject.grades.find((group) => group.key === gradeKey) || null
    : null;

  const subjectLabel = (group) =>
    group.key === UNPLACED_SUBJECT ? translate("Other lessons") : translate(group.name);

  // In the board's own word, so an Indian school reads "Class 10" and an
  // American one "Grade 10" — a teacher browsing their own syllabus should not
  // have to translate the label on the way past.
  //
  // "not set" rather than a guess when the year is missing. The field is
  // derived from the curriculum, so a lesson the derivation could not place has
  // no year, and saying so is the only honest label available. Naming it also
  // keeps it tappable, which is the point: an unplaceable lesson still has to
  // be reachable, or the tree quietly holds fewer lessons than the library.
  const gradeLabel = (group) => {
    const copyForBoard = gradeCopyFor(group.board);
    return group.key === UNPLACED_GRADE
      ? translate(copyForBoard.unset)
      : fill(translate(copyForBoard.label), { grade: group.grade });
  };

  // Nothing seeded: the toggle would open onto an empty tree, which is a worse
  // answer than not offering it.
  if (groups.length === 0) return null;

  const leafRows = grade ? (
    <View testID="lesson-browse-lessons" style={styles.lessons}>
      {grade.lessons.map((lesson) => (
        <ResultRow
          key={lesson.id}
          lesson={lesson}
          progress={progressByLesson[lesson.id]}
          colors={colors}
          isDarkMode={isDarkMode}
          onOpen={onOpenLesson}
          onEdit={onEditLesson}
          t={t}
        />
      ))}
    </View>
  ) : null;

  return (
    <View testID="lesson-browse" style={[styles.wrap, isPage && styles.wrapPage]}>
      <TouchableOpacity
        testID="lesson-browse-toggle"
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded: open }}
        accessibilityLabel={translate(copy.label)}
        style={[styles.toggle, isPage && styles.togglePage]}
      >
        <Text style={[styles.toggleText, { color: colors.textSecondary }]}>
          {translate(copy.label)}
        </Text>
        <MaterialCommunityIcons
          name={open ? "chevron-up" : "chevron-down"}
          size={16}
          color={colors.textSecondary}
        />
      </TouchableOpacity>

      {open ? (
        <View
          testID="lesson-browse-panel"
          style={[styles.panel, { borderColor: colors.border }]}
        >
          {subject ? (
            <View testID="lesson-browse-breadcrumb" style={styles.crumbs}>
              <Crumb
                testID="lesson-browse-crumb-root"
                label={translate("All subjects")}
                colors={colors}
                onPress={() => {
                  setSubjectKey(null);
                  setGradeKey(null);
                }}
                t={translate}
              />
              <Text style={[styles.crumbSep, { color: colors.textMuted }]}>›</Text>
              <Crumb
                testID="lesson-browse-crumb-subject"
                label={subjectLabel(subject)}
                colors={colors}
                onPress={grade ? () => setGradeKey(null) : null}
                t={translate}
              />
              {grade ? (
                <>
                  <Text style={[styles.crumbSep, { color: colors.textMuted }]}>›</Text>
                  <Crumb label={gradeLabel(grade)} colors={colors} t={translate} />
                </>
              ) : null}
            </View>
          ) : (
            <Text style={[styles.hint, { color: colors.textMuted }]}>
              {translate(copy.hint)}
            </Text>
          )}

          {!subject ? (
            <View testID="lesson-browse-subjects" style={styles.chips}>
              {groups.map((group) => (
                <BrowseChip
                  key={group.key}
                  testID={`lesson-browse-subject-${group.key}`}
                  label={subjectLabel(group)}
                  count={group.count}
                  colors={colors}
                  onPress={() => setSubjectKey(group.key)}
                  t={translate}
                />
              ))}
            </View>
          ) : !grade ? (
            <View testID="lesson-browse-grades" style={styles.chips}>
              {subject.grades.map((group) => (
                <BrowseChip
                  key={group.key}
                  testID={`lesson-browse-grade-${group.key}`}
                  label={gradeLabel(group)}
                  count={group.count}
                  colors={colors}
                  onPress={() => setGradeKey(group.key)}
                  t={translate}
                />
              ))}
            </View>
          ) : isPage ? (
            leafRows
          ) : (
            <ScrollView
              testID="lesson-browse-lessons-scroll"
              style={styles.leafCapped}
              nestedScrollEnabled
            >
              {leafRows}
            </ScrollView>
          )}
        </View>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  wrap: { marginTop: 12 },
  // Lines up under the box rather than running wider than the thing it belongs
  // to, exactly as the result list does.
  wrapPage: { width: "100%", maxWidth: PAGE_BOX_MAX, alignSelf: "center", marginTop: 16 },

  // No border, no fill, no accent. The box above is the primary path; this is
  // the quiet way round, and it has to read as one.
  toggle: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 5,
    paddingVertical: 6,
    paddingHorizontal: 2,
  },
  togglePage: { alignSelf: "center" },
  toggleText: { fontSize: 13, fontWeight: "600" },

  panel: { marginTop: 6, borderTopWidth: 1, paddingTop: 12, gap: 10 },
  hint: { fontSize: 12 },

  crumbs: { flexDirection: "row", alignItems: "center", flexWrap: "wrap", gap: 6 },
  crumbLink: { fontSize: 12, fontWeight: "700" },
  crumbCurrent: { fontSize: 12, fontWeight: "700" },
  crumbSep: { fontSize: 12 },

  chips: { flexDirection: "row", flexWrap: "wrap", gap: 8 },
  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 7,
    borderWidth: 1,
    borderRadius: 999,
    paddingLeft: 13,
    paddingRight: 8,
    paddingVertical: 8,
  },
  chipLabel: { fontSize: 13, fontWeight: "700" },
  chipCount: { borderRadius: 999, paddingHorizontal: 7, paddingVertical: 1 },
  chipCountText: { fontSize: 11, fontWeight: "800" },

  lessons: { gap: 10 },
  leafCapped: { maxHeight: SECTION_LEAF_MAX },
});

export default LessonBrowseTree;
